import { cloneDeep, get, isEqual, isPlainObject, set as setValue } from 'lodash'
import { nanoid } from 'nanoid'
import { useEffect } from 'react'

import { STORE_NAME } from '~/constants'
import EStoreStateKeys from '~/models/store'
import { store } from '~/store'

const STORE_SYNC_CHANNEL = `${STORE_NAME}-store-sync`
const STORE_SYNC_MESSAGE_TYPE = 'store-sync'
const SYNC_META_KEY = 'syncPathUpdatedAt'
const COALESCE_PATCHES_DELAY_MS = 50

type TStoreState = ReturnType<typeof store.getState>
type TPathVersion = {
  updatedAt: number
  counter: number
  tabId: string
}
type TPathVersions = Record<string, TPathVersion>

type TStorePatch = {
  path: string
  value: unknown
  version?: TPathVersion
}

type TQueuedStorePatch = TStorePatch & {
  allowUnversionedPatch?: boolean
}

type TStoreSyncMessage = {
  type: typeof STORE_SYNC_MESSAGE_TYPE
  patches: TStorePatch[]
}

const excludedRootKeys = new Set<string>([
  EStoreStateKeys.chatbot,
  'resetStore',
  SYNC_META_KEY,
])

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

const isStoreSyncMessage = (value: unknown): value is TStoreSyncMessage => {
  if (!isRecord(value)) {
    return false
  }

  return value.type === STORE_SYNC_MESSAGE_TYPE && Array.isArray(value.patches)
}

const getPathVersions = (state: TStoreState): TPathVersions =>
  state.syncPathUpdatedAt ?? {}

const getRootKey = (path: string): string => path.split('.')[0] || ''

const isSyncableRootKey = (key: string, value: unknown): boolean =>
  !excludedRootKeys.has(key) && typeof value !== 'function'

const getSyncableRootKeys = (state: TStoreState): string[] => {
  const stateRecord = state as Record<string, unknown>

  return Object.keys(stateRecord).filter((key) =>
    isSyncableRootKey(key, stateRecord[key]),
  )
}

const shouldDiffChildren = (previousValue: unknown, nextValue: unknown) =>
  isPlainObject(previousValue) && isPlainObject(nextValue)

const getChangedPaths = (
  previousValue: unknown,
  nextValue: unknown,
  path: string[] = [],
): string[] => {
  if (isEqual(previousValue, nextValue)) {
    return []
  }

  if (shouldDiffChildren(previousValue, nextValue)) {
    const previousRecord = previousValue as Record<string, unknown>
    const nextRecord = nextValue as Record<string, unknown>
    const keys = new Set([
      ...Object.keys(previousRecord),
      ...Object.keys(nextRecord),
    ])

    return Array.from(keys).flatMap((key) =>
      getChangedPaths(previousRecord[key], nextRecord[key], [...path, key]),
    )
  }

  return path.length ? [path.join('.')] : []
}

const getNextVersion = (
  versions: TPathVersions,
  changedPaths: string[],
  tabId: string,
): TPathVersion => ({
  updatedAt: Date.now(),
  counter:
    Math.max(0, ...changedPaths.map((path) => versions[path]?.counter ?? 0)) +
    1,
  tabId,
})

const isIncomingVersionNewer = (
  incomingVersion: TPathVersion | undefined,
  currentVersion: TPathVersion | undefined,
): boolean => {
  if (!incomingVersion) {
    return false
  }

  if (!currentVersion) {
    return true
  }

  if (incomingVersion.updatedAt !== currentVersion.updatedAt) {
    return incomingVersion.updatedAt > currentVersion.updatedAt
  }

  if (incomingVersion.counter !== currentVersion.counter) {
    return incomingVersion.counter > currentVersion.counter
  }

  return incomingVersion.tabId > currentVersion.tabId
}

const buildPatches = (
  state: TStoreState,
  paths: string[],
  versions: TPathVersions,
): TStorePatch[] =>
  paths.map((path) => ({
    path,
    value: get(state, path),
    version: versions[path],
  }))

const getPersistedState = (value: string | null): TStoreState | null => {
  if (!value) {
    return null
  }

  try {
    const parsedValue = JSON.parse(value) as { state?: TStoreState }

    return parsedValue.state ?? null
  } catch {
    return null
  }
}

const isResetStorageSnapshot = (state: TStoreState): boolean => {
  const { user } = state

  return !user?.id && !user?.impersonatorsUserId
}

const getChangedSyncablePaths = (
  previousState: TStoreState,
  nextState: TStoreState,
): string[] => {
  const previousStateRecord = previousState as Record<string, unknown>
  const nextStateRecord = nextState as Record<string, unknown>

  return getSyncableRootKeys(nextState).flatMap((key) =>
    getChangedPaths(previousStateRecord[key], nextStateRecord[key], [key]),
  )
}

const useSyncStore = () => {
  useEffect(() => {
    let isApplyingRemoteState = false
    let isHydrating = !store.persist.hasHydrated()
    let shouldSkipNextHydrationChange = isHydrating
    let pendingPatches: TStorePatch[] = []
    let pendingRemotePatchesByPath: Record<string, TQueuedStorePatch> = {}
    let coalesceTimeoutId: ReturnType<typeof setTimeout> | null = null
    const tabId = nanoid()

    const channel =
      typeof BroadcastChannel === 'undefined'
        ? null
        : new BroadcastChannel(STORE_SYNC_CHANNEL)

    const postPatches = (patches: TStorePatch[]) => {
      if (!patches.length) {
        return
      }

      channel?.postMessage({
        type: STORE_SYNC_MESSAGE_TYPE,
        patches,
      } satisfies TStoreSyncMessage)
    }

    const publishOrQueuePatches = (patches: TStorePatch[]) => {
      if (!isHydrating) {
        postPatches(patches)
        return
      }

      pendingPatches = [...pendingPatches, ...patches]
    }

    const flushPendingPatches = () => {
      postPatches(pendingPatches)
      pendingPatches = []
    }

    const applyPatchesIfNewer = (
      patches: TQueuedStorePatch[],
      options: {
        allowUnversionedPatches?: boolean
        forceUnversionedPatches?: boolean
      } = {},
    ) => {
      const currentState = store.getState()
      const currentVersions = getPathVersions(currentState)
      const nextVersions = { ...currentVersions }
      const nextState = cloneDeep(currentState) as Record<string, unknown>
      let hasChanges = false

      patches.forEach(({ path, value, version, allowUnversionedPatch }) => {
        const rootKey = getRootKey(path)
        const currentVersion = currentVersions[path]
        const shouldAcceptUnversionedPatch =
          (options.allowUnversionedPatches || allowUnversionedPatch) &&
          !version &&
          !currentVersion
        const shouldForceUnversionedPatch =
          options.forceUnversionedPatches && !version

        if (
          !isSyncableRootKey(rootKey, value) ||
          (!shouldForceUnversionedPatch &&
            !shouldAcceptUnversionedPatch &&
            !isIncomingVersionNewer(version, currentVersion))
        ) {
          return
        }

        setValue(nextState, path, value)
        nextVersions[path] =
          version ?? getNextVersion(nextVersions, [path], tabId)
        hasChanges = true
      })

      if (!hasChanges) {
        return
      }

      try {
        isApplyingRemoteState = true
        store.setState({
          ...nextState,
          syncPathUpdatedAt: nextVersions,
        } as Partial<TStoreState>)
      } finally {
        isApplyingRemoteState = false
      }
    }

    const queueRemotePatches = (
      patches: TQueuedStorePatch[],
      options: { allowUnversionedPatches?: boolean } = {},
    ) => {
      patches.forEach((patch) => {
        const queuedPatch = {
          ...patch,
          allowUnversionedPatch:
            patch.allowUnversionedPatch || options.allowUnversionedPatches,
        }
        const existingPatch = pendingRemotePatchesByPath[patch.path]
        const shouldReplaceExistingPatch =
          !existingPatch ||
          isIncomingVersionNewer(queuedPatch.version, existingPatch.version)

        if (shouldReplaceExistingPatch) {
          pendingRemotePatchesByPath[patch.path] = queuedPatch
        }
      })

      if (coalesceTimeoutId) {
        clearTimeout(coalesceTimeoutId)
      }

      coalesceTimeoutId = setTimeout(() => {
        const patchesToApply = Object.values(pendingRemotePatchesByPath)

        pendingRemotePatchesByPath = {}
        coalesceTimeoutId = null
        applyPatchesIfNewer(patchesToApply)
      }, COALESCE_PATCHES_DELAY_MS)
    }

    const unsubscribe = store.subscribe((state, previousState) => {
      if (isApplyingRemoteState || isHydrating) {
        if (isHydrating && shouldSkipNextHydrationChange) {
          shouldSkipNextHydrationChange = false
        }
        return
      }

      if (isApplyingRemoteState) {
        return
      }

      const changedPaths = getChangedSyncablePaths(previousState, state)

      if (!changedPaths.length) {
        return
      }

      const currentVersions = getPathVersions(state)
      const nextVersion = getNextVersion(currentVersions, changedPaths, tabId)
      const nextVersions = {
        ...currentVersions,
        ...Object.fromEntries(changedPaths.map((path) => [path, nextVersion])),
      }

      store.setState({ syncPathUpdatedAt: nextVersions })
      publishOrQueuePatches(buildPatches(state, changedPaths, nextVersions))
    })

    const handleMessage = (event: MessageEvent<unknown>) => {
      if (!isStoreSyncMessage(event.data)) {
        return
      }

      queueRemotePatches(event.data.patches)
    }

    const handleStorage = (event: StorageEvent) => {
      if (event.key !== STORE_NAME) {
        return
      }

      if (!event.newValue) {
        try {
          isApplyingRemoteState = true
          store.getState().resetStore()
        } finally {
          isApplyingRemoteState = false
        }
        return
      }

      const persistedState = getPersistedState(event.newValue)

      if (!persistedState) {
        return
      }

      const persistedVersions = getPathVersions(persistedState)
      const isUnversionedStorageSnapshot =
        !Object.keys(persistedVersions).length
      const currentState = store.getState()
      const changedPaths = getChangedSyncablePaths(currentState, persistedState)
      const patches = buildPatches(
        persistedState,
        changedPaths,
        persistedVersions,
      )

      if (
        isUnversionedStorageSnapshot &&
        isResetStorageSnapshot(persistedState)
      ) {
        applyPatchesIfNewer(patches, { forceUnversionedPatches: true })
        return
      }

      queueRemotePatches(patches, { allowUnversionedPatches: true })
    }

    const unsubscribeHydrate = store.persist.onHydrate(() => {
      isHydrating = true
      shouldSkipNextHydrationChange = true
    })
    const unsubscribeFinishHydration = store.persist.onFinishHydration(() => {
      isHydrating = false
      shouldSkipNextHydrationChange = false
      flushPendingPatches()
    })

    channel?.addEventListener('message', handleMessage)
    window.addEventListener('storage', handleStorage)

    return () => {
      unsubscribe()
      unsubscribeHydrate()
      unsubscribeFinishHydration()
      if (coalesceTimeoutId) {
        clearTimeout(coalesceTimeoutId)
      }
      channel?.removeEventListener('message', handleMessage)
      channel?.close()
      window.removeEventListener('storage', handleStorage)
    }
  }, [])
}

export default useSyncStore
